import { useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import { Container, ContainerLayout, StickyPageHeader } from '@/_containers';
import type { SystemUserQuerySchema } from '@/_pages/system-users/shared';
import { queryClient } from '@/_shared';
import type { WidgetModelName } from '@/_shared/routes-widgets/configs/permissible-widget-keys';

import { WidgetSkeletonLoader } from './shared/components/widget-skeleton-loader';
import type { WidgetSettingValueSchema } from './shared/settings';
import { getWidgetByModelName, WIDGET_SIZE_MAP } from './shared/widgets-size-map';

const SkeletonWrapper = ({ className, children }: { className: string; children: React.ReactNode }) => (
  <div className={twMerge('border-2 border-transparent', className)}>{children}</div>
);

export function Loading() {
  const skeletonItems: { name: string; sizeClassName: string; showFooter: boolean }[] | null = useMemo(() => {
    const user = queryClient.getQueryData<SystemUserQuerySchema>(['CURRENT_USER']);

    if (!user) {
      // Render nothing or a default loader (if needed)
      return null;
    }

    const currentSettings = user.custom_settings;

    // Filter for widget settings (those with order and size properties)
    const currentWidgets = currentSettings?.filter((setting) => {
      return setting.value && typeof setting.value === 'object' && 'order' in setting.value && 'size' in setting.value;
    });

    // If no widgets in cache, return default (3 medium-sized cards)
    if (!currentWidgets || currentWidgets.length === 0) {
      return Array(3).fill({ sizeClassName: WIDGET_SIZE_MAP['medium'], showFooter: true, name: 'default' });
    }

    // Map the cached widgets to skeleton items with appropriate sizes
    return currentWidgets.map((widget) => {
      const widgetValue = widget.value as unknown as WidgetSettingValueSchema;
      const size = widgetValue?.size || 'medium';
      return {
        name: widget.name,
        sizeClassName: WIDGET_SIZE_MAP[size],
        showFooter: getWidgetByModelName(widget.name as WidgetModelName)?.withFooter || false,
      };
    });
  }, []);

  return (
    <ContainerLayout>
      <StickyPageHeader title="Dashboard" />
      <Container className="grid grid-cols-12 gap-4">
        {skeletonItems?.map((item, i) => (
          <SkeletonWrapper key={i} className={item.sizeClassName}>
            {getWidgetByModelName(item.name as WidgetModelName)?.loader ?? (
              <WidgetSkeletonLoader className="h-full w-full" showFooter={item.showFooter} />
            )}
          </SkeletonWrapper>
        ))}
      </Container>
    </ContainerLayout>
  );
}
